import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { API, PlayerCullDTO, ScreenFullDTO, TimingRelation } from '../../../services/api/api';
import { TranslateService } from '@ngx-translate/core';
import { SbLocalDateTime } from '../../../services/api/api-date-time';
import { UtilService } from '../../../services/util.service';
import { DateTime } from 'luxon';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NavigationService } from '@ngx-seatback/utils';
import { ErrorService } from '../../../services/error.service';
import { ToasterService, ToastType } from '../../../services/toaster/toaster.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { SbModalState } from '../../../services/gui/modal/modal-state.service';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { enumKeys } from '../../../services/global/global-methods';

@Component({
  selector: 'app-inventory-cart',
  templateUrl: './inventory-cart.component.html',
  styleUrls: ['./inventory-cart.component.scss'],
  standalone: false,
})
export class InventoryCartComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly modalState: SbModalState,
    private readonly modal: ModalService,
    private readonly toaster: ToasterService,
    private readonly api: API,
    private readonly errorHandler: ErrorService,
    public readonly translate: TranslateService,
    public readonly util: UtilService,
    private readonly navigationService: NavigationService
  ) {
    this.initEnums();
  }

  @Input() isInModalContext: boolean;
  modalType: string;
  _from?: SbLocalDateTime;
  _till?: SbLocalDateTime;
  amount: number = 10000;
  selectedTimingRelation: TimingRelation;
  timingRelations: TimingRelation[] = [];

  cull: PlayerCullDTO = new PlayerCullDTO();

  durationPerPlayout: number = 10; // TODO

  calculatedSum: number = 0;

  debounceTimer: ReturnType<typeof setTimeout> = null;

  form = new UntypedFormGroup({
    Name: new UntypedFormControl('', [Validators.required]),
  });

  get Name() {
    return this.form.get('Name');
  }

  get from(): SbLocalDateTime {
    return this._from;
  }

  set from(val) {
    this._from = val;
  }

  get till(): SbLocalDateTime {
    return this._till;
  }

  set till(val) {
    this._till = val;
  }

  @Input() set cartFilter(val: any) {
    this._cartFilter = val;
  }

  get cartFilter(): any {
    return this._cartFilter;
  }

  @Input() set selectedScreens(val: number[]) {
    this._selectedScreens = val;
    this.requestPriceDebounced();
  }

  get selectedScreens(): any {
    return this._selectedScreens;
  }

  _cartFilter: any;
  _selectedScreens: number[] = [];

  @Output() removeFromCart: EventEmitter<any> = new EventEmitter<any>();

  ngOnInit(): void {
    if (!this.from) {
      this.from = new SbLocalDateTime(DateTime.local().endOf('month'));
    }
    if (!this.till) {
      this.till = new SbLocalDateTime(DateTime.local().endOf('month'));
    }
    if (!this.selectedTimingRelation) {
      this.selectedTimingRelation = TimingRelation.WholePeriod;
    }
    if (this.isInModalContext) {
      if (this.modalState.options.get('inventorySelectionModal')?.payload) {
        this.modalType = this.modalState.options.get('inventorySelectionModal').payload.type;
      }
    }
  }

  initEnums() {
    const timingKeys = enumKeys(TimingRelation);
    this.timingRelations = [];
    for (const val of timingKeys) {
      this.timingRelations.push(TimingRelation[val]);
    }
  }

  handleDateChange(event: SbLocalDateTime, type: string) {
    if (type === 'from') {
      this.from = event; // .fromDate;
    }
    if (type === 'till') {
      this.till = event; // .toDate;
    }

    this.requestPriceDebounced();
  }

  setValue(key: string, value: string) {
    if (!value) {
      return;
    }
    if (this.cull && key === 'name') {
      this.cull.name = value;
    }
  }

  convertToScreenDTO(screenIds: number[]) {
    const screens: ScreenFullDTO[] = [];
    for (const id of screenIds) {
      const screenDTO = new ScreenFullDTO();
      screenDTO.id = id;
      screens.push(screenDTO);
    }
    return screens;
  }

  addToCull() {
    if (this.isInModalContext && this.modalState.modals.get('inventorySelectionModal')) {
      this.modalState.modals.get('inventorySelectionModal').close(this.convertToScreenDTO(this.selectedScreens));
    }
  }

  convertToPlayerCull() {
    if (!this.cull) {
      return; // todo check if throw error
    }
    this.cull.staticElements = this.convertToScreenDTO(this.selectedScreens);
    this.cull.invertedStatic = false;
    this.cull.type = 'PlayerCull';
    this.api
      .cs_Cull_checkNameAvailability({
        type: this.cull.type,
        name: this.cull.name,
      })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: creationPossible => {
          if (creationPossible) {
            this.api
              .cs_Cull_createPlayerCullClientScoped(this.cull)
              .pipe(takeUntilDestroyed(this.destroyRef), take(1))
              .subscribe({
                next: data => {
                  if (data) {
                    this.toaster.toast(
                      ToastType.SUCCESS,
                      this.translate.instant('lang.globals.itemSuccessfullyCreated', { itemName: data.name })
                    );
                    if (this.isInModalContext) {
                      this.modal.close('inventorySelectionModal', data);
                    } else {
                      this.navigationService.navigateToPoint(RoutingID.filterDetails, [
                        {
                          id: 'filterID',
                          value: data?.id,
                        },
                      ]);
                    }
                    this.cull = new PlayerCullDTO();
                    this.selectedScreens = [];
                  }
                },
                error: error => {
                  this.errorHandler.errorHandler(error);
                },
              });
          } else {
            this.toaster.toast(ToastType.DANGER, this.translate.instant('lang.globals.alreadyExists'));
          }
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  isCreationPossible(): boolean {
    return !(this.cull?.name && this.selectedScreens.length > 0);
  }

  requestPriceDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.resolveScreenPrice();
    }, 500);
  }

  resolveScreenPrice() {
    if (this.selectedScreens.length > 0) {
      this.api
        .ds_rm_Inventory_calculatePricing(this.amount, {
          screen_id: this.selectedScreens,
          from: this.from,
          till: this.till,
          timingRelation: this.selectedTimingRelation,
          durationPlayout: this.durationPerPlayout,
        })
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: data => {
            this.calculatedSum = parseFloat((data as unknown as number).toFixed(2));
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.calculatedSum = 0;
    }
  }
}
